import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { LiveKitRoom, RoomAudioRenderer, useTracks, useParticipants, useLocalParticipant, useRoomContext, VideoTrack } from "@livekit/components-react";
import { Track } from "livekit-client";
import "@livekit/components-styles";
import { Radio, Users, Mic, MicOff, Video, VideoOff, Pin, Star, Hand, X, ArrowLeft, Grid3x3, Maximize2,
  Columns2, PictureInPicture, Wand2, Clapperboard, MessageSquareText, BarChart3, MonitorUp, ShieldCheck,
  UserPlus, UserMinus, Ban, CheckCircle2, Send, ScrollText, Gauge, PenTool, Presentation as PresIcon, Settings,
  Lock, Activity, AlertTriangle, Search, Camera, ShieldAlert } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLiveRealtime } from "../lib/useLiveRealtime";
import Whiteboard from "../components/stage/Whiteboard";
import Presentation from "../components/stage/Presentation";
import { toast } from "sonner";

const LAYOUTS = [["auto", "Auto", Wand2], ["grid", "Grid", Grid3x3], ["spotlight", "Spotlight", Maximize2], ["split", "Split", Columns2], ["pip", "PiP", PictureInPicture]];
const errMsg = (e, f = "Action failed.") => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x?.msg).join(", ") : (d?.error || f); };

export default function StageRoom() {
  const { sessionId } = useParams();
  const { user, authApi } = useAuth();
  const [session, setSession] = useState(null);
  const [conn, setConn] = useState(null);
  const [stage, setStage] = useState(null);
  const [joining, setJoining] = useState(false);
  const [lastRt, setLastRt] = useState(null);

  const loadStage = useCallback(() => { api.get(`/live/sessions/${sessionId}/stage`, { headers: user ? { Authorization: `Bearer ${localStorage.getItem("taven_user_token")}` } : {} }).then(({ data }) => setStage(data)).catch(() => {}); }, [sessionId, user]);

  useEffect(() => { api.get(`/live/sessions/${sessionId}`).then(({ data }) => setSession(data)).catch(() => {}); loadStage(); }, [sessionId, loadStage]);

  const onRealtime = useCallback((type, data) => { setLastRt({ type, data, ts: Date.now() }); if (type && type.startsWith("stage")) loadStage(); }, [loadStage]);
  const { send: rtSend, status: rtStatus } = useLiveRealtime(session && session.status !== "ended" ? sessionId : null, user?.first_name || "Guest", onRealtime);

  // Graceful degradation: when the realtime WS isn't connected, poll stage state so the consent
  // notice, panic banner, admit updates and audit timeline still refresh (never miss consent).
  useEffect(() => {
    if (!conn || rtStatus === "connected") return;
    const id = setInterval(loadStage, 5000);
    return () => clearInterval(id);
  }, [conn, rtStatus, loadStage]);

  const enterStage = async () => {
    if (!user) { toast.error("Sign in to join the stage."); return; }
    setJoining(true);
    try {
      await authApi.post(`/live/sessions/${sessionId}/stage/join`);
      const { data } = await authApi.get(`/live/sessions/${sessionId}/stage/token`);
      setConn(data); loadStage();
    } catch (e) { toast.error(errMsg(e, "Could not join the stage.")); }
    finally { setJoining(false); }
  };

  if (!session) return <div className="py-24 text-center bg-taven-black min-h-screen text-white">Loading studio…</div>;
  if (stage && !stage.enabled) return <div className="py-24 text-center bg-taven-black min-h-screen text-white">The multi-guest stage is not enabled here.</div>;

  return (
    <div className="fixed inset-0 z-[60] bg-taven-black text-white overflow-hidden">
      {!conn ? (
        <div className="flex flex-col items-center justify-center min-h-screen gap-5 px-6" data-testid="stage-lobby">
          <Clapperboard className="w-14 h-14 text-taven-gold" />
          <h1 className="text-2xl font-black font-display">{session.title}</h1>
          <p className="text-white/50 text-sm text-center max-w-md">TAVEN Studio — a multi-guest live stage with Director Mode. Join to appear on stage or enter the waiting room.</p>
          <button onClick={enterStage} disabled={joining} data-testid="enter-stage-btn" className="bg-taven-gold text-black px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-white disabled:opacity-40">{joining ? "Joining…" : "Enter Studio"}</button>
          <Link to={`/live/${sessionId}`} className="text-xs text-white/40 hover:text-white">Watch as viewer instead</Link>
        </div>
      ) : (
        <LiveKitRoom serverUrl={conn.url} token={conn.token} connect video={conn.can_publish} audio={conn.can_publish} style={{ height: "100%" }}>
          <StageStudio sessionId={sessionId} session={session} stage={stage} me={conn} user={user} authApi={authApi} reload={loadStage} rtSend={rtSend} lastRt={lastRt} />
          <RoomAudioRenderer />
        </LiveKitRoom>
      )}
    </div>
  );
}

function StageStudio({ sessionId, session, stage, me, user, authApi, reload, rtSend, lastRt }) {
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();
  const room = useRoomContext();
  const camTracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: true }], { onlySubscribed: false });
  const screenTracks = useTracks([{ source: Track.Source.ScreenShare, withPlaceholder: false }]);
  const [panel, setPanel] = useState("participants");
  const [overlay, setOverlay] = useState("none"); // none | board | present
  const [devices, setDevices] = useState({ videoinput: [], audioinput: [], audiooutput: [] });
  const [showDevices, setShowDevices] = useState(false);
  const [hearts, setHearts] = useState([]);
  const [consentReq, setConsentReq] = useState(null); // incoming media request for me
  const [confirmState, setConfirmState] = useState(null); // { message, onConfirm }
  const boardRef = useRef(null);

  const role = stage?.my_role || "viewer";
  const canDirect = role === "host" || role === "cohost";
  const canModerate = canDirect || role === "moderator";
  const isHost = role === "host";
  const control = stage?.control || {};
  const layout = stage?.layout || "auto";
  const onStage = stage?.on_stage || [];
  const waiting = stage?.waiting || [];
  const recById = {}; [...(stage?.on_stage || []), ...(stage?.waiting || [])].forEach((p) => { recById[p.identity] = p; });

  // report connection quality (health telemetry)
  useEffect(() => {
    const id = setInterval(() => {
      const q = localParticipant?.connectionQuality;
      const map = { excellent: "excellent", good: "good", poor: "poor", lost: "lost" };
      if (q && authApi) authApi.post(`/live/sessions/${sessionId}/stage/quality`, { quality: map[q] || "good" }).catch(() => {});
    }, 8000);
    return () => clearInterval(id);
  }, [localParticipant, sessionId, authApi]);

  // Live Health + Recovery: report reconnection events so host dashboard + audit see them; reload on reconnect
  useEffect(() => {
    if (!room) return;
    const send = (event) => authApi?.post(`/live/sessions/${sessionId}/stage/health`, { event }).catch(() => {});
    const onReconnecting = () => { send("reconnecting"); toast.message("Reconnecting…"); };
    const onReconnected = () => { send("reconnected"); toast.success("Reconnected — restoring your permissions"); reload(); };
    const onDisconnected = () => send("disconnected");
    room.on("reconnecting", onReconnecting);
    room.on("reconnected", onReconnected);
    room.on("disconnected", onDisconnected);
    return () => { room.off("reconnecting", onReconnecting); room.off("reconnected", onReconnected); room.off("disconnected", onDisconnected); };
  }, [room, sessionId, authApi, reload]);

  // Guest consent notice: surface any pending media request directed at me
  useEffect(() => {
    const reqs = stage?.my_consent_requests || [];
    if (reqs.length) { if (!consentReq || !reqs.find((r) => r.id === consentReq.id)) setConsentReq(reqs[0]); }
    else if (consentReq) setConsentReq(null);
  }, [stage, consentReq]);

  const askConfirm = (message, onConfirm) => setConfirmState({ message, onConfirm });
  const DESTRUCTIVE = { remove: "Remove this participant from the stage?", block: "Block this participant from rejoining? They will not be able to return.", reject: "Reject this waiting guest?", end_guest: "End this guest's participation?" };
  const manage = async (email, action) => {
    const run = async () => { try { await authApi.post(`/live/sessions/${sessionId}/stage/manage`, { email, action }); reload(); toast.success(`${action.replace(/_/g, " ")} ✓`); } catch (e) { toast.error(errMsg(e)); } };
    if (DESTRUCTIVE[action]) askConfirm(DESTRUCTIVE[action], run); else run();
  };
  const requestConsent = async (email, source) => { try { await authApi.post(`/live/sessions/${sessionId}/stage/consent/request`, { email, source }); toast.success(`Asked to turn on ${source}`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const respondConsent = async (accept) => { if (!consentReq) return; const rid = consentReq.id, src = consentReq.source; setConsentReq(null); try { await authApi.post(`/live/sessions/${sessionId}/stage/consent/respond`, { request_id: rid, accept }); reload(); toast.success(accept ? `${src} enabled — you're in control` : `Request declined`); } catch (e) { toast.error(errMsg(e)); reload(); } };
  const revokeConsent = async (source) => { try { await authApi.post(`/live/sessions/${sessionId}/stage/consent/revoke`, { source }); reload(); toast.success(`${source} turned off`); } catch (e) { toast.error(errMsg(e)); } };
  const panic = (action) => askConfirm(action === "engage" ? "Engage PANIC mode? This locks new joins, freezes promotions, stops all screen shares, and mutes stage audio. Cameras are never forced on or off." : "Recover from panic mode and restore normal controls?", async () => { try { await authApi.post(`/live/sessions/${sessionId}/stage/panic`, { action }); reload(); toast.success(action === "engage" ? "Panic mode engaged" : "Recovered"); } catch (e) { toast.error(errMsg(e)); } });
  const setLayout = async (l) => { try { await authApi.post(`/live/sessions/${sessionId}/stage/layout`, { layout: l }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const applyScene = async (pid) => { try { await authApi.post(`/live/sessions/${sessionId}/stage/scene`, { preset_id: pid }); reload(); toast.success("Scene applied"); } catch (e) { toast.error(errMsg(e)); } };
  const raiseHand = async () => { try { await authApi.post(`/live/sessions/${sessionId}/stage/hand`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const toggleScreen = async (on) => { try { if (on && localParticipant) await localParticipant.setScreenShareEnabled(true); else if (localParticipant) await localParticipant.setScreenShareEnabled(false); await authApi.post(`/live/sessions/${sessionId}/stage/screenshare`, { on }); reload(); } catch (e) { toast.error("Screen share unavailable in this browser."); } };
  const react = () => { rtSend?.({ type: "reaction", kind: "heart" }); spawnHeart(); };
  const spawnHeart = () => { const id = Math.random(); setHearts((h) => [...h, id]); setTimeout(() => setHearts((h) => h.filter((x) => x !== id)), 2000); };

  // ---- mid-live device switching (no refresh, with rollback + audit) ----
  const loadDevices = async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      setDevices({ videoinput: all.filter((d) => d.kind === "videoinput"), audioinput: all.filter((d) => d.kind === "audioinput"), audiooutput: all.filter((d) => d.kind === "audiooutput") });
      setShowDevices((v) => !v);
    } catch { toast.error("Devices unavailable in this browser."); }
  };
  const switchDevice = async (kind, deviceId) => {
    const lkKind = { videoinput: "videoinput", audioinput: "audioinput", audiooutput: "audiooutput" }[kind];
    try {
      await room.switchActiveDevice(lkKind, deviceId);
      const auditKind = { videoinput: "camera", audioinput: "microphone", audiooutput: "speaker" }[kind];
      authApi.post(`/live/sessions/${sessionId}/stage/device`, { kind: auditKind, value: deviceId.slice(0, 24) }).catch(() => {});
      toast.success(`Switched ${auditKind}`);
    } catch { toast.error("That device failed — keeping the current one."); } // graceful rollback (LiveKit keeps prior track)
  };

  // tile ordering per layout
  const screenActive = stage?.screen_share || screenTracks.length > 0;
  const spotlightId = stage?.spotlight;
  const camByIdentity = {}; camTracks.forEach((t) => { camByIdentity[t.participant?.identity] = t; });
  const stageIdentities = onStage.map((p) => p.identity);
  // include any live participant even if stage record lagging
  participants.forEach((p) => { if (!stageIdentities.includes(p.identity)) stageIdentities.push(p.identity); });

  const gridCols = stageIdentities.length <= 1 ? "grid-cols-1" : stageIdentities.length <= 4 ? "grid-cols-2" : stageIdentities.length <= 9 ? "grid-cols-3" : "grid-cols-4";

  const Tile = ({ identity }) => {
    const rec = recById[identity] || {};
    const track = camByIdentity[identity];
    const isSpot = identity === spotlightId;
    return (
      <div className={`relative bg-[#111] border ${isSpot ? "border-taven-gold" : "border-white/10"} overflow-hidden`} data-testid={`stage-tile-${rec.email || identity}`}>
        {track && track.publication ? <VideoTrack trackRef={track} className="w-full h-full object-cover" /> : (
          <div className="w-full h-full min-h-[120px] flex items-center justify-center bg-gradient-to-br from-[#1a1a1a] to-black">
            <span className="w-14 h-14 rounded-full bg-taven-gold/20 border border-taven-gold flex items-center justify-center font-black text-taven-gold">{(rec.name || "?")[0]?.toUpperCase()}</span>
          </div>
        )}
        <div className="absolute bottom-0 inset-x-0 flex items-center justify-between px-2 py-1 bg-gradient-to-t from-black/80 to-transparent">
          <span className="text-[11px] font-bold flex items-center gap-1 truncate">
            {rec.role === "host" && <Star className="w-3 h-3 text-taven-gold" />}
            {rec.muted && <MicOff className="w-3 h-3 text-red-400" />}
            {rec.name || "Guest"} {rec.role && rec.role !== "guest" && <span className="text-taven-gold/70">· {rec.role}</span>}
          </span>
          <span className="flex items-center gap-1">
            {rec.role !== "host" && rec.role !== "cohost" && (
              <>
                <Camera className={`w-3 h-3 ${rec.consent?.camera ? "text-green-400" : "text-white/25"}`} title={rec.consent?.camera ? "Camera consent granted" : "No camera consent"} />
                <Mic className={`w-3 h-3 ${rec.consent?.microphone ? "text-green-400" : "text-white/25"}`} title={rec.consent?.microphone ? "Mic consent granted" : "No mic consent"} />
              </>
            )}
            {rec.quality && rec.quality !== "unknown" && <Gauge className={`w-3 h-3 ${rec.quality === "poor" || rec.quality === "lost" ? "text-red-400" : "text-green-400"}`} title={`Connection: ${rec.quality}`} />}
          </span>
        </div>
        {canModerate && rec.email && rec.email !== user?.email && (
          <div className="absolute top-1 right-1 flex flex-wrap gap-1 justify-end max-w-[90%] opacity-0 hover:opacity-100 focus-within:opacity-100 transition-opacity" data-testid={`tile-controls-${rec.email}`}>
            {canDirect && rec.role !== "host" && rec.role !== "cohost" && (
              <>
                {!rec.consent?.camera && <button onClick={() => requestConsent(rec.email, "camera")} title="Ask to turn on camera" data-testid={`request-cam-${rec.email}`} className="p-1 bg-black/70 hover:bg-taven-gold hover:text-black"><Camera className="w-3 h-3" /></button>}
                {!rec.consent?.microphone && <button onClick={() => requestConsent(rec.email, "microphone")} title="Ask to turn on mic" data-testid={`request-mic-${rec.email}`} className="p-1 bg-black/70 hover:bg-taven-gold hover:text-black"><Mic className="w-3 h-3" /></button>}
              </>
            )}
            <button onClick={() => manage(rec.email, rec.muted ? "unmute" : "mute")} title={rec.muted ? "Allow unmute" : "Mute"} className="p-1 bg-black/70 hover:bg-taven-gold hover:text-black"><MicOff className="w-3 h-3" /></button>
            <button onClick={() => manage(rec.email, rec.cam_disabled ? "restore_camera" : "disable_camera")} title={rec.cam_disabled ? "Allow camera" : "Turn camera off"} className="p-1 bg-black/70 hover:bg-taven-gold hover:text-black"><VideoOff className="w-3 h-3" /></button>
            <button onClick={() => manage(rec.email, isSpot ? "unspotlight" : "spotlight")} title="Spotlight" className="p-1 bg-black/70 hover:bg-taven-gold hover:text-black"><Maximize2 className="w-3 h-3" /></button>
            {canDirect && <button onClick={() => manage(rec.email, "remove")} title="Remove" className="p-1 bg-black/70 hover:bg-red-500"><X className="w-3 h-3" /></button>}
          </div>
        )}
      </div>
    );
  };

  const spotlightTiles = spotlightId ? [spotlightId] : stageIdentities.slice(0, 1);
  const otherTiles = stageIdentities.filter((i) => !spotlightTiles.includes(i));

  return (
    <div className="flex flex-col lg:flex-row h-full" data-testid="stage-studio">
      {/* main stage */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10">
          <Link to={`/live/${sessionId}`} className="flex items-center gap-1.5 text-xs font-bold uppercase text-white/70 hover:text-white"><ArrowLeft className="w-4 h-4" /> Exit</Link>
          <span className="flex items-center gap-2 text-sm font-bold"><Clapperboard className="w-4 h-4 text-taven-gold" /> {session.title}</span>
          <span className="flex items-center gap-1 text-xs text-white/60" data-testid="stage-onstage-count"><Users className="w-3.5 h-3.5" /> {onStage.length} on stage</span>
        </div>

        <div className="flex-1 relative p-3 overflow-hidden" data-testid="stage-canvas">
          {control.panic && (
            <div className="absolute top-2 inset-x-2 z-30 flex items-center gap-2 px-3 py-2 bg-red-600/90 border border-red-300 text-white text-xs font-bold" data-testid="panic-banner">
              <ShieldAlert className="w-4 h-4" /> PANIC MODE ACTIVE — new joins locked, promotions frozen, screen shares stopped, stage audio muted. Cameras are untouched.
              {isHost && <button onClick={() => panic("recover")} data-testid="panic-recover-btn" className="ml-auto bg-white text-red-700 px-2 py-1 text-[10px] uppercase font-black hover:bg-red-100">Recover</button>}
            </div>
          )}
          {overlay === "board" && <Whiteboard sessionId={sessionId} lastRt={lastRt} rtSend={rtSend} onClose={() => setOverlay("none")} askConfirm={askConfirm} />}
          {overlay === "present" && <Presentation sessionId={sessionId} lastRt={lastRt} onStage={me.can_publish} onClose={() => setOverlay("none")} />}
          {hearts.map((id) => <Heart key={id} />)}
          {screenActive ? (
            <div className="grid grid-rows-[1fr_auto] gap-2 h-full">
              <div className="bg-black border border-taven-gold flex items-center justify-center" data-testid="screen-share-view">{screenTracks[0] ? <VideoTrack trackRef={screenTracks[0]} className="w-full h-full object-contain" /> : <span className="text-white/40 text-sm flex items-center gap-2"><MonitorUp className="w-5 h-5" /> Screen sharing active</span>}</div>
              <div className="flex gap-2 overflow-x-auto">{stageIdentities.map((i) => <div key={i} className="w-40 shrink-0 aspect-video"><Tile identity={i} /></div>)}</div>
            </div>
          ) : layout === "spotlight" ? (
            <div className="grid grid-rows-[1fr_auto] gap-2 h-full">
              <div className="min-h-0">{spotlightTiles.map((i) => <div key={i} className="h-full"><Tile identity={i} /></div>)}</div>
              {otherTiles.length > 0 && <div className="flex gap-2 overflow-x-auto">{otherTiles.map((i) => <div key={i} className="w-40 shrink-0 aspect-video"><Tile identity={i} /></div>)}</div>}
            </div>
          ) : layout === "pip" ? (
            <div className="relative h-full">
              <div className="h-full">{spotlightTiles.map((i) => <div key={i} className="h-full"><Tile identity={i} /></div>)}</div>
              <div className="absolute bottom-3 right-3 w-48 aspect-video">{otherTiles[0] && <Tile identity={otherTiles[0]} />}</div>
            </div>
          ) : layout === "split" ? (
            <div className="grid grid-cols-2 gap-2 h-full">{stageIdentities.slice(0, 2).map((i) => <Tile key={i} identity={i} />)}</div>
          ) : (
            <div className={`grid ${gridCols} gap-2 h-full auto-rows-fr`} data-testid="stage-grid">{stageIdentities.map((i) => <Tile key={i} identity={i} />)}</div>
          )}
        </div>

        {/* bottom action bar */}
        <div className="flex items-center justify-center gap-2 px-4 py-3 border-t border-white/10 flex-wrap relative" data-testid="stage-actionbar">
          {me.can_publish && <>
            <BarBtn onClick={() => localParticipant?.setMicrophoneEnabled(!localParticipant?.isMicrophoneEnabled)} icon={Mic} label="Mic" testid="self-mic" />
            <BarBtn onClick={() => localParticipant?.setCameraEnabled(!localParticipant?.isCameraEnabled)} icon={Video} label="Cam" testid="self-cam" />
            <BarBtn onClick={() => toggleScreen(!screenActive)} icon={MonitorUp} label="Share" testid="self-share" active={screenActive} />
            <div className="relative">
              <BarBtn onClick={loadDevices} icon={Settings} label="Devices" testid="self-devices" active={showDevices} />
              {showDevices && (
                <div className="absolute bottom-14 left-1/2 -translate-x-1/2 w-64 bg-[#151515] border border-white/15 p-3 space-y-2 z-40" data-testid="device-menu">
                  {[["videoinput", "Camera"], ["audioinput", "Microphone"], ["audiooutput", "Speaker"]].map(([k, l]) => (
                    <div key={k}>
                      <label className="text-[10px] font-bold uppercase text-white/50">{l}</label>
                      <select onChange={(e) => switchDevice(k, e.target.value)} data-testid={`device-switch-${k}`} className="w-full bg-white/5 border border-white/15 px-2 py-1.5 text-xs mt-0.5">
                        <option value="">Default</option>
                        {devices[k].map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || `${l} ${d.deviceId.slice(0, 4)}`}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>}
          <BarBtn onClick={() => setOverlay(overlay === "board" ? "none" : "board")} icon={PenTool} label="Board" testid="open-whiteboard" active={overlay === "board"} />
          <BarBtn onClick={() => setOverlay(overlay === "present" ? "none" : "present")} icon={PresIcon} label="Present" testid="open-presentation" active={overlay === "present"} />
          <BarBtn onClick={raiseHand} icon={Hand} label="Hand" testid="self-hand" />
          <BarBtn onClick={react} icon={Star} label="React" testid="self-react" />
        </div>
      </div>

      {/* right control rail */}
      <div className="w-full lg:w-[340px] border-l border-white/10 flex flex-col min-h-0">
        {canDirect && <DirectorBar layout={layout} presets={stage?.presets || []} onLayout={setLayout} onScene={applyScene} scene={stage?.scene_preset} isHost={isHost} control={control} onPanic={panic} />}
        <div className="flex flex-wrap border-b border-white/10 text-[10px] font-bold uppercase">
          {[["participants", "People", Users], ["privacy", "Privacy", ShieldCheck], ["polls", "Polls", BarChart3], ["qa", "Q&A", MessageSquareText], canModerate && ["health", "Health", Activity], canModerate && ["audit", "Timeline", ScrollText]].filter(Boolean).map(([k, l, I]) => (
            <button key={k} onClick={() => setPanel(k)} data-testid={`panel-tab-${k}`} className={`flex-1 min-w-[33%] flex items-center justify-center gap-1 py-2.5 ${panel === k ? "bg-taven-gold/15 text-taven-gold border-b-2 border-taven-gold" : "text-white/50"}`}><I className="w-3.5 h-3.5" />{l}</button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {panel === "participants" && <PeoplePanel waiting={waiting} onStage={onStage} canModerate={canModerate} canDirect={canDirect} manage={manage} hands={stage?.hands || []} me={user?.email} />}
          {panel === "privacy" && <PrivacyPanel myConsent={stage?.my_consent || {}} history={stage?.my_consent_history || []} pending={stage?.my_consent_requests || []} onRevoke={revokeConsent} onRespond={respondConsent} setConsentReq={setConsentReq} />}
          {panel === "polls" && <PollsPanel sessionId={sessionId} authApi={authApi} polls={stage?.polls || []} canModerate={canModerate} reload={reload} />}
          {panel === "qa" && <QAPanel sessionId={sessionId} authApi={authApi} qa={stage?.qa || []} canModerate={canModerate} reload={reload} />}
          {panel === "health" && canModerate && <HealthPanel health={stage?.health} pendingConsent={stage?.pending_consent || []} />}
          {panel === "audit" && canModerate && <AuditPanel sessionId={sessionId} authApi={authApi} />}
        </div>
      </div>

      {consentReq && <ConsentModal req={consentReq} onRespond={respondConsent} />}
      {confirmState && <ConfirmModal state={confirmState} onClose={() => setConfirmState(null)} />}
    </div>
  );
}

const BarBtn = ({ onClick, icon: I, label, testid, active }) => (
  <button onClick={onClick} data-testid={testid} className={`flex flex-col items-center gap-0.5 px-3 py-1.5 ${active ? "text-taven-gold" : "text-white/70 hover:text-white"}`}><I className="w-5 h-5" /><span className="text-[9px] font-bold uppercase">{label}</span></button>
);

const Heart = () => <div className="absolute bottom-16 left-1/2 text-taven-orange animate-bounce" style={{ animation: "floatUp 2s ease-out forwards" }}><Star className="w-6 h-6 fill-current" /></div>;

function DirectorBar({ layout, presets, onLayout, onScene, scene, isHost, control, onPanic }) {
  return (
    <div className="border-b border-white/10 p-3" data-testid="director-bar">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-taven-gold flex items-center gap-1.5"><Clapperboard className="w-3.5 h-3.5" /> Director Mode</p>
        {isHost && (control?.panic
          ? <button onClick={() => onPanic("recover")} data-testid="panic-recover-rail" className="flex items-center gap-1 px-2 py-1 text-[10px] font-black uppercase bg-red-600 text-white hover:bg-red-500"><ShieldAlert className="w-3 h-3" /> Recover</button>
          : <button onClick={() => onPanic("engage")} data-testid="panic-engage-btn" className="flex items-center gap-1 px-2 py-1 text-[10px] font-black uppercase border border-red-500 text-red-400 hover:bg-red-600 hover:text-white"><ShieldAlert className="w-3 h-3" /> Panic</button>)}
      </div>
      <div className="flex flex-wrap gap-1 mb-2">
        {LAYOUTS.map(([k, l, I]) => <button key={k} onClick={() => onLayout(k)} data-testid={`layout-${k}`} className={`flex items-center gap-1 px-2 py-1.5 text-[10px] font-bold uppercase border ${layout === k ? "border-taven-gold bg-taven-gold/15 text-taven-gold" : "border-white/15 text-white/60 hover:border-white/40"}`}><I className="w-3 h-3" />{l}</button>)}
      </div>
      <p className="text-[10px] text-white/40 mb-1">Scene presets</p>
      <div className="flex flex-wrap gap-1">
        {presets.map((p) => <button key={p.id} onClick={() => onScene(p.id)} data-testid={`scene-${p.id}`} className={`px-2 py-1 text-[10px] font-bold uppercase border ${scene === p.id ? "border-taven-gold bg-taven-gold text-black" : "border-white/15 text-white/70 hover:border-taven-gold"}`}>{p.name}</button>)}
      </div>
    </div>
  );
}

function ConsentModal({ req, onRespond }) {
  const label = { camera: "camera", microphone: "microphone", screen: "screen sharing" }[req.source] || req.source;
  const Icon = req.source === "camera" ? Camera : req.source === "microphone" ? Mic : MonitorUp;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm px-6" data-testid="consent-modal">
      <div className="w-full max-w-sm bg-[#141414] border border-taven-gold/40 p-6 text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-taven-gold/15 border border-taven-gold flex items-center justify-center mb-3"><Icon className="w-6 h-6 text-taven-gold" /></div>
        <h3 className="text-lg font-black font-display mb-1">Turn on your {label}?</h3>
        <p className="text-xs text-white/60 mb-1">The host is asking to enable your {label}. You are in full control — nothing turns on unless you allow it.</p>
        <p className="text-[10px] text-white/35 mb-5">You can turn it back off at any time from the Privacy tab.</p>
        <div className="flex gap-3">
          <button onClick={() => onRespond(false)} data-testid="consent-decline-btn" className="flex-1 py-2.5 text-xs font-bold uppercase border border-white/20 text-white/70 hover:bg-white/10">Not now</button>
          <button onClick={() => onRespond(true)} data-testid="consent-allow-btn" className="flex-1 py-2.5 text-xs font-bold uppercase bg-taven-gold text-black hover:bg-white">Allow</button>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ state, onClose }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm px-6" data-testid="confirm-modal">
      <div className="w-full max-w-sm bg-[#141414] border border-red-500/40 p-6">
        <div className="flex items-center gap-2 mb-3 text-red-400"><AlertTriangle className="w-5 h-5" /><h3 className="text-base font-black font-display">Please confirm</h3></div>
        <p className="text-sm text-white/70 mb-5">{state.message}</p>
        <div className="flex gap-3">
          <button onClick={onClose} data-testid="confirm-cancel-btn" className="flex-1 py-2.5 text-xs font-bold uppercase border border-white/20 text-white/70 hover:bg-white/10">Cancel</button>
          <button onClick={() => { state.onConfirm(); onClose(); }} data-testid="confirm-ok-btn" className="flex-1 py-2.5 text-xs font-bold uppercase bg-red-600 text-white hover:bg-red-500">Confirm</button>
        </div>
      </div>
    </div>
  );
}

function PrivacyPanel({ myConsent, history, pending, onRevoke, onRespond, setConsentReq }) {
  const sources = [["camera", "Camera", Camera], ["microphone", "Microphone", Mic], ["screen", "Screen share", MonitorUp]];
  return (
    <div className="space-y-4" data-testid="privacy-panel">
      <div>
        <p className="text-[11px] font-bold uppercase text-taven-gold mb-2 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> My active permissions</p>
        {sources.map(([k, l, I]) => (
          <div key={k} className="flex items-center justify-between border border-white/10 p-2 mb-1.5" data-testid={`privacy-${k}`}>
            <span className="text-xs font-semibold flex items-center gap-1.5"><I className={`w-3.5 h-3.5 ${myConsent[k] ? "text-green-400" : "text-white/30"}`} />{l}<span className={`text-[10px] ${myConsent[k] ? "text-green-400" : "text-white/40"}`}>· {myConsent[k] ? "Allowed" : "Off"}</span></span>
            {myConsent[k] && <button onClick={() => onRevoke(k)} data-testid={`revoke-${k}`} className="text-[10px] font-bold uppercase text-red-400 hover:text-red-300 border border-red-500/40 px-2 py-0.5">Turn off</button>}
          </div>
        ))}
      </div>
      {pending.length > 0 && (
        <div>
          <p className="text-[11px] font-bold uppercase text-taven-gold mb-2">Pending requests</p>
          {pending.map((r) => (
            <div key={r.id} className="border border-taven-gold/40 p-2 mb-1.5" data-testid={`pending-request-${r.source}`}>
              <p className="text-xs mb-1.5">Host asked to turn on your <b>{r.source}</b>.</p>
              <div className="flex gap-2"><button onClick={() => { setConsentReq(r); }} className="text-[10px] font-bold uppercase bg-white/10 px-2 py-1 hover:bg-white/20">Review</button></div>
            </div>
          ))}
        </div>
      )}
      <div>
        <p className="text-[11px] font-bold uppercase text-white/50 mb-2">Recent consent activity</p>
        {history.length === 0 ? <p className="text-xs text-white/30">No consent requests this session.</p> : history.map((r) => (
          <div key={r.id} className="text-[11px] border border-white/10 p-1.5 mb-1">
            <span className="text-white/80">{r.source}</span> · <span className={r.status === "accepted" ? "text-green-400" : r.status === "declined" ? "text-red-400" : "text-white/40"}>{r.status}</span>
            <span className="block text-white/30 text-[9px]">{new Date(r.created_at).toLocaleTimeString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function HealthPanel({ health, pendingConsent }) {
  if (!health) return <p className="text-xs text-white/30">Health data unavailable.</p>;
  const stat = (label, value, warn) => (
    <div className={`border ${warn ? "border-red-500/50" : "border-white/10"} p-2`}>
      <p className={`text-lg font-black ${warn ? "text-red-400" : "text-taven-gold"}`}>{value}</p>
      <p className="text-[9px] uppercase text-white/40">{label}</p>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="health-panel">
      <p className="text-[11px] font-bold uppercase text-taven-gold flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" /> Live Health</p>
      <div className="grid grid-cols-2 gap-2">
        {stat("Participants", health.participant_count)}
        {stat("On stage", health.on_stage_count)}
        {stat("Waiting", health.waiting_count)}
        {stat("Poor connections", health.poor_connections, health.poor_connections > 0)}
      </div>
      {pendingConsent.length > 0 && (
        <div>
          <p className="text-[10px] uppercase text-white/40 mb-1">Awaiting consent ({pendingConsent.length})</p>
          {pendingConsent.map((r) => <div key={r.id} className="text-[11px] border border-taven-gold/30 p-1.5 mb-1">{r.target_name || r.target_email} — {r.source} <span className="text-white/40">requested</span></div>)}
        </div>
      )}
      <div>
        <p className="text-[10px] uppercase text-white/40 mb-1">Reconnection & media events</p>
        {(health.events || []).length === 0 ? <p className="text-xs text-white/30">No events yet.</p> : (health.events || []).map((e) => (
          <div key={e.id} className="text-[11px] border border-white/10 p-1.5 mb-1">
            <span className={e.event === "disconnected" ? "text-red-400" : e.event === "reconnected" ? "text-green-400" : "text-white/70"}>{e.event}</span> · {e.name} {e.quality && <span className="text-white/40">({e.quality})</span>}
            <span className="block text-white/30 text-[9px]">{new Date(e.created_at).toLocaleTimeString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PeoplePanel({ waiting, onStage, canModerate, canDirect, manage, hands, me }) {
  return (
    <div className="space-y-4" data-testid="people-panel">
      {canModerate && (
        <div data-testid="waiting-room">
          <p className="text-[11px] font-bold uppercase text-taven-gold mb-2">Waiting Room ({waiting.length})</p>
          {waiting.length === 0 ? <p className="text-xs text-white/30">No one waiting.</p> : waiting.map((p) => (
            <div key={p.email} className="flex items-center justify-between gap-2 border border-white/10 p-2 mb-1.5" data-testid={`waiting-${p.email}`}>
              <span className="text-xs font-semibold flex items-center gap-1 truncate">{hands.includes(p.email) && <Hand className="w-3 h-3 text-taven-gold" />}{p.name}</span>
              <div className="flex gap-1">
                <button onClick={() => manage(p.email, "admit")} data-testid={`admit-${p.email}`} title="Admit" className="p-1 bg-green-600 hover:bg-green-700"><CheckCircle2 className="w-3.5 h-3.5" /></button>
                <button onClick={() => manage(p.email, "reject")} data-testid={`reject-${p.email}`} title="Reject" className="p-1 bg-white/10 hover:bg-red-500"><X className="w-3.5 h-3.5" /></button>
                {canDirect && <button onClick={() => manage(p.email, "block")} title="Block" className="p-1 bg-white/10 hover:bg-red-600"><Ban className="w-3.5 h-3.5" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}
      <div>
        <p className="text-[11px] font-bold uppercase text-taven-gold mb-2">On Stage ({onStage.length})</p>
        {onStage.map((p) => (
          <div key={p.email} className="flex items-center justify-between gap-2 border border-white/10 p-2 mb-1.5">
            <span className="text-xs font-semibold flex items-center gap-1 truncate">{p.role === "host" && <Star className="w-3 h-3 text-taven-gold" />}{p.name} <span className="text-white/40">· {p.role}</span></span>
            {canDirect && p.email !== me && p.role !== "host" && (
              <div className="flex gap-1">
                <button onClick={() => manage(p.email, "promote_cohost")} data-testid={`promote-cohost-${p.email}`} title="Co-host" className="p-1 bg-white/10 hover:bg-taven-gold hover:text-black"><ShieldCheck className="w-3.5 h-3.5" /></button>
                <button onClick={() => manage(p.email, "promote_moderator")} data-testid={`promote-mod-${p.email}`} title="Moderator" className="p-1 bg-white/10 hover:bg-taven-gold hover:text-black"><UserPlus className="w-3.5 h-3.5" /></button>
                <button onClick={() => manage(p.email, "return_to_waiting")} title="To waiting" className="p-1 bg-white/10 hover:bg-white/20"><UserMinus className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PollsPanel({ sessionId, authApi, polls, canModerate, reload }) {
  const [q, setQ] = useState(""); const [opts, setOpts] = useState(["", ""]);
  const create = async () => { try { await authApi.post(`/live/sessions/${sessionId}/stage/poll`, { question: q, options: opts.filter((o) => o.trim()) }); setQ(""); setOpts(["", ""]); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const vote = async (pid, i) => { try { await authApi.post(`/live/sessions/${sessionId}/stage/poll/${pid}/vote`, { option: i }); reload(); } catch (e) { toast.error(errMsg(e, "Already voted")); } };
  const close = async (pid) => { try { await authApi.post(`/live/sessions/${sessionId}/stage/poll/${pid}/close`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-3" data-testid="polls-panel">
      {canModerate && (
        <div className="border border-white/10 p-2 space-y-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Poll question" data-testid="poll-question" className="w-full bg-white/5 border border-white/15 px-2 py-1.5 text-xs" />
          {opts.map((o, i) => <input key={i} value={o} onChange={(e) => setOpts((a) => a.map((x, j) => j === i ? e.target.value : x))} placeholder={`Option ${i + 1}`} data-testid={`poll-opt-${i}`} className="w-full bg-white/5 border border-white/15 px-2 py-1.5 text-xs" />)}
          <div className="flex gap-2"><button onClick={() => setOpts((a) => [...a, ""])} className="text-[10px] text-taven-gold">+ option</button><button onClick={create} data-testid="poll-create" className="ml-auto bg-taven-gold text-black px-3 py-1 text-[10px] font-bold uppercase">Launch</button></div>
        </div>
      )}
      {polls.map((p) => { const total = p.options.reduce((a, o) => a + o.votes, 0) || 1; return (
        <div key={p.id} className="border border-white/10 p-2" data-testid={`poll-${p.id}`}>
          <p className="text-xs font-bold mb-1.5">{p.question} {!p.open && <span className="text-white/40">(closed)</span>}</p>
          {p.options.map((o, i) => (
            <button key={i} disabled={!p.open} onClick={() => vote(p.id, i)} data-testid={`poll-vote-${p.id}-${i}`} className="w-full text-left mb-1 relative border border-white/10 overflow-hidden disabled:cursor-default">
              <div className="absolute inset-0 bg-taven-gold/20" style={{ width: `${(o.votes / total) * 100}%` }} />
              <span className="relative flex justify-between px-2 py-1 text-[11px]"><span>{o.text}</span><span className="text-white/60">{o.votes}</span></span>
            </button>
          ))}
          {canModerate && p.open && <button onClick={() => close(p.id)} className="text-[10px] text-white/50 hover:text-red-400 mt-1">Close poll</button>}
        </div>
      ); })}
      {polls.length === 0 && <p className="text-xs text-white/30">No polls yet.</p>}
    </div>
  );
}

function QAPanel({ sessionId, authApi, qa, canModerate, reload }) {
  const [q, setQ] = useState("");
  const ask = async () => { if (!q.trim()) return; try { await authApi.post(`/live/sessions/${sessionId}/stage/qa`, { question: q }); setQ(""); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const act = async (id, status) => { try { await authApi.post(`/live/sessions/${sessionId}/stage/qa/${id}`, { status }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-2" data-testid="qa-panel">
      <div className="flex gap-2"><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask a question…" data-testid="qa-input" className="flex-1 bg-white/5 border border-white/15 px-2 py-1.5 text-xs" /><button onClick={ask} data-testid="qa-send" className="bg-taven-gold text-black px-3"><Send className="w-3.5 h-3.5" /></button></div>
      {qa.map((item) => (
        <div key={item.id} className="border border-white/10 p-2 flex items-start justify-between gap-2" data-testid={`qa-${item.id}`}>
          <div><p className="text-[10px] text-taven-gold font-bold">{item.name}</p><p className="text-xs">{item.question}</p></div>
          {canModerate && <div className="flex gap-1 shrink-0"><button onClick={() => act(item.id, "answered")} title="Answered" className="p-1 bg-green-600"><CheckCircle2 className="w-3 h-3" /></button><button onClick={() => act(item.id, "dismissed")} title="Dismiss" className="p-1 bg-white/10 hover:bg-red-500"><X className="w-3 h-3" /></button></div>}
        </div>
      ))}
      {qa.length === 0 && <p className="text-xs text-white/30">No questions yet.</p>}
    </div>
  );
}

function AuditPanel({ sessionId, authApi }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const load = useCallback(() => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (filter) params.action = filter;
    authApi.get(`/live/sessions/${sessionId}/stage/audit`, { params }).then(({ data }) => setRows(data.audit || [])).catch(() => {});
  }, [sessionId, authApi, q, filter]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  const FILTERS = [["", "All"], ["consent", "Consent"], ["mute", "Mute"], ["remove", "Remove"], ["block", "Block"], ["promote", "Promote"], ["panic", "Panic"]];
  return (
    <div className="space-y-2" data-testid="audit-panel">
      <p className="text-[10px] text-white/40 flex items-center gap-1"><ScrollText className="w-3 h-3" /> Immutable moderation & consent timeline</p>
      <div className="flex items-center gap-1 border border-white/15 px-2"><Search className="w-3 h-3 text-white/40" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search actor / target / action" data-testid="audit-search" className="flex-1 bg-transparent py-1.5 text-xs outline-none" /></div>
      <div className="flex flex-wrap gap-1">
        {FILTERS.map(([k, l]) => <button key={k} onClick={() => setFilter(k)} data-testid={`audit-filter-${k || "all"}`} className={`px-2 py-0.5 text-[10px] font-bold uppercase border ${filter === k ? "border-taven-gold bg-taven-gold/15 text-taven-gold" : "border-white/15 text-white/50"}`}>{l}</button>)}
      </div>
      {rows.map((a) => (
        <div key={a.id} className="text-[11px] border border-white/10 p-1.5" data-testid={`audit-row-${a.id}`}>
          <span className="text-taven-gold font-bold">{a.action}</span> · {a.target_email} <span className="text-white/40">by {a.actor_email} ({a.actor_role})</span>
          <span className="block text-white/30 text-[9px]">{new Date(a.created_at).toLocaleString()}</span>
        </div>
      ))}
      {rows.length === 0 && <p className="text-xs text-white/30">No matching events.</p>}
    </div>
  );
}
